import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { getAgentAvailabilityStatus } from '../services/usersService';
import { setManualLogoutFlag, disconnectWebSockets, AUTH_STORAGE_KEYS } from '../lib/api/client';
import { queryClient } from '../lib/api/queryClient';
import { useConversationsStore } from '../components/chat/hooks/FolderUseChat/conversationsStore';

const AuthContext = createContext();

// Map array indices to named constants for readability
const [ACCESS_TOKEN, REFRESH_TOKEN, USER, CURRENT_TENANT, AVAILABLE_TENANTS] = AUTH_STORAGE_KEYS;

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
};

const getAuthStorage = () => ({
  user: localStorage.getItem(USER),
  token: localStorage.getItem(ACCESS_TOKEN),
  refreshToken: localStorage.getItem(REFRESH_TOKEN),
  currentTenant: localStorage.getItem(CURRENT_TENANT),
  availableTenants: localStorage.getItem(AVAILABLE_TENANTS)
});

const setAuthStorage = (user, token, refreshToken, currentTenant, availableTenants) => {
  if (user) localStorage.setItem(USER, JSON.stringify(user));
  if (token) localStorage.setItem(ACCESS_TOKEN, token);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN, refreshToken);
  if (currentTenant) localStorage.setItem(CURRENT_TENANT, JSON.stringify(currentTenant));
  if (availableTenants) localStorage.setItem(AVAILABLE_TENANTS, JSON.stringify(availableTenants));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const logoutTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const lastRefreshAttemptRef = useRef(0);
  const REFRESH_COOLDOWN_MS = 5000;

  // Fetches the agent's current availability status from the server.
  // Called once after login/session-restore, and again whenever the
  // sidebar profile card is hovered - not on every render/navigation.
  const fetchAvailabilityStatus = async () => {
    try {
      const response = await getAgentAvailabilityStatus();
      const statusValue = response?.data?.availability_status ?? response?.availability_status;
      if (statusValue) {
        setAvailabilityStatus(statusValue);
      }
      return statusValue;
    } catch (error) {
      console.error('❌ Failed to fetch agent availability status:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storage = getAuthStorage();
      
      if (storage.user && storage.token) {
        try {
          const userData = JSON.parse(storage.user);
          const tenantData = storage.currentTenant ? JSON.parse(storage.currentTenant) : null;
          const tenantsData = storage.availableTenants ? JSON.parse(storage.availableTenants) : [];
          
          authService.setAuthToken(storage.token);
          
          try {
            const verification = await authService.verifyToken(storage.token);
            if (verification.isValid) {
              setUser(userData);
              setToken(storage.token);
              setRefreshToken(storage.refreshToken);
              setCurrentTenant(tenantData);
              setAvailableTenants(tenantsData);
              fetchAvailabilityStatus();
            } else {
              console.warn('⚠️ Stored token is invalid, clearing session');
              if (verification.shouldClearToken) {
                clearAuthStorage();
                authService.clearAuthToken();
              }
            }
          } catch (error) {
            console.error('❌ Error verifying token:', error);
          }
        } catch (error) {
          console.error('❌ Error restoring session:', error);
          clearAuthStorage();
          authService.clearAuthToken();
        }
      }
      setLoading(false);
    };

    setTimeout(initializeAuth, 100);
  }, []);

  /**
   * After access token expires: call refresh token API only.
   * If refresh API success → just refresh/update the access token and reschedule.
   * If refresh token has also expired (refresh API fails) → clear session only (no logout API).
   * Guards prevent infinite loop when token is already expired (re-entry + cooldown).
   */
  useEffect(() => {
    if (!token) {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
      isRefreshingRef.current = false;
      return;
    }

    const tryRefreshAndReschedule = async () => {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          setToken(refreshResult.accessToken);
          setRefreshToken(refreshResult.refreshToken);
          authService.updateAxiosHeaders();
        } else {
          await logout({ skipLogoutApi: true });
        }
      } finally {
        isRefreshingRef.current = false;
        lastRefreshAttemptRef.current = Date.now();
      }
    };

    const scheduleExpiryCheck = (tokenToCheck) => {
      const expiryMs = authService.getTokenExpiryMs(tokenToCheck);
      if (expiryMs <= 0) {
        const now = Date.now();
        if (now - lastRefreshAttemptRef.current < REFRESH_COOLDOWN_MS) return;
        tryRefreshAndReschedule();
        return;
      }
      logoutTimeoutRef.current = setTimeout(async () => {
        logoutTimeoutRef.current = null;
        const currentToken = authService.getCurrentToken();
        if (!currentToken) {
          logout();
          return;
        }
        const remainingMs = authService.getTokenExpiryMs(currentToken);
        if (remainingMs > 0) {
          scheduleExpiryCheck(currentToken);
          return;
        }
        tryRefreshAndReschedule();
      }, expiryMs);
    };

    scheduleExpiryCheck(token);

    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
    };
  }, [token]);

  /**
   * Update auth state from login data (used by React Query mutations)
   * This allows mutations to handle API calls while AuthContext manages state
   */
  const setLoginData = (loginData) => {
    if (!loginData || !loginData.user || !loginData.tokens) {
      console.error('❌ Invalid login data provided');
      return { success: false, error: { message: 'Invalid login data' } };
    }

    const { user, tokens } = loginData;
    const accessToken = tokens?.access;
    const refreshTokenValue = tokens?.refresh;
    
    if (accessToken) {
      queryClient.clear();
      queryClient.resetQueries();

      const tenantInfo = user.tenant_info || [];
      const activeTenantId = user.active_tenant_id;
      const currentTenantData = tenantInfo.find(tenant => tenant.id === activeTenantId) || tenantInfo[0] || null;

      setUser(user);
      setToken(accessToken);
      setRefreshToken(refreshTokenValue);
      setCurrentTenant(currentTenantData);
      setAvailableTenants(tenantInfo);
      fetchAvailabilityStatus();

      setAuthStorage(user, accessToken, refreshTokenValue, currentTenantData, tenantInfo);
      
      // Set tokens in authService for API client
      authService.setTokens(accessToken, refreshTokenValue);
      
      return { 
        success: true, 
        data: loginData,
        user,
        token: accessToken,
        refreshToken: refreshTokenValue,
        currentTenant: currentTenantData,
        availableTenants: tenantInfo
      };
    }
    
    console.error('❌ No access token in login data');
    return { 
      success: false, 
      error: { message: 'No access token received from server' } 
    };
  };

  const switchTenant = (tenant) => {
    if (tenant && availableTenants.find(t => t.id === tenant.id)) {
      setCurrentTenant(tenant);
      localStorage.setItem(CURRENT_TENANT, JSON.stringify(tenant));
      return { success: true, tenant };
    }
    
    console.error('❌ Invalid tenant or tenant not available');
    return { success: false, error: 'Invalid tenant' };
  };

  /**
   * Logout - clears state and storage. Optionally calls logout API.
   * @param {Object} options
   * @param {boolean} options.skipLogoutApi - If true, skip calling logout API (e.g. when refresh token expired).
   */
  const logout = async (options = {}) => {
    const { skipLogoutApi = false } = options;

    setManualLogoutFlag(true);

    try {
      if (!skipLogoutApi) {
        const currentToken = token;
        if (currentToken) {
          try {
            await authService.logout('Manual logout');
            console.log('✅ Logout completed successfully');
          } catch (error) {
            console.warn('⚠️ Logout API call failed (continuing with local logout):', error.message);
          }
        }
      }
    } finally {
      // Clear and reset React Query cache on logout (no stale data from previous session)
      queryClient.clear();
      queryClient.resetQueries();

      // Clear chat conversations store (All/My/Inbox/Alerts lists) so next login shows fresh data
      try {
        useConversationsStore.getState().clearAll();
      } catch (e) {
        console.warn('Conversations store clear on logout:', e?.message);
      }

      localStorage.removeItem('conversationMessages');
      localStorage.removeItem('tabConversations');
      localStorage.removeItem('chat_active_tab');
      localStorage.removeItem('chat_selected_chat_id');

      Object.keys(localStorage)
        .filter((key) => key.startsWith('lastRead_'))
        .forEach((key) => localStorage.removeItem(key));

      // Always clear local state and storage
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      setCurrentTenant(null);
      setAvailableTenants([]);
      setAvailabilityStatus(null);
      clearAuthStorage();
      authService.clearAuthToken();
      
      // Disconnect WebSockets using shared utility
      await disconnectWebSockets();
      
      console.log('✅ React Query cache cleared and reset');
    }
  };

  const verifyToken = async (tokenToVerify = null) => {
    try {
      const verification = await authService.verifyToken(tokenToVerify);
      
      if (verification.isValid) {
        return { success: true, data: verification.data };
      } else {
        console.warn('⚠️ Token verification failed:', verification.error);
        if (verification.shouldClearToken) {
          logout();
        }
        return { success: false, error: verification.error };
      }
    } catch (error) {
      console.error('❌ Token verification error:', error);
      return { success: false, error: error.message };
    }
  };

  const refreshTokenManually = async () => {
    try {
      const refreshResult = await authService.refreshAccessToken();
      
      if (refreshResult.success) {
        // Update both tokens in context state
        setToken(refreshResult.accessToken);
        setRefreshToken(refreshResult.refreshToken);
        
        // Ensure axios headers are updated for all future requests
        authService.updateAxiosHeaders();
        
        return { success: true, data: refreshResult.data };
      } else {
        console.warn('⚠️ Token refresh failed:', refreshResult.error);
        if (refreshResult.shouldLogout) {
          logout();
        }
        return { success: false, error: refreshResult.error };
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return { success: false, error: error.message };
    }
  };

  const updateUser = (nextUser) => {
    if (!nextUser) return;
    setUser(nextUser);
    localStorage.setItem(USER, JSON.stringify(nextUser));
  };

  const value = {
    user,
    token,
    refreshToken,
    currentTenant,
    availableTenants,
    loading,
    availabilityStatus,
    setAvailabilityStatus,
    fetchAvailabilityStatus,
    setLoginData, // Expose for React Query mutations to update auth state
    logout,
    verifyToken,
    refreshTokenManually,
    switchTenant,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 